import React, {useState, useEffect, useRef} from "react";
import {authenticationService} from "../../Scripts/authentication.service";
import Title from "../../Comps/Title";
import {useParams} from "react-router-dom";
import {handleResponse} from "../../Scripts/handle-response";
import Loading from "../../Loading/Loading";
import ReactCanvasConfetti from "react-canvas-confetti";
import SpinningWheel from "../Comps/Wheel";
import {Dialog, DialogPanel} from "@headlessui/react";
import {XMarkIcon} from "@heroicons/react/24/outline";
import CssWheel from "../Comps/Wheel2";

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

const WheelView = () => {
    const [user] = useState(authenticationService.currentUserValue)
    const { id } = useParams()
    const [pubs, setPubs] = useState([])
    const [wheel, setWheel] = useState([])
    const [modifiers, setModifiers] = useState([])
    const [modifiersOpen, setModifiersOpen] = useState(false)
    const [selectedModifer, setSelectedModifier] = useState(null)
    const [loaded, setLoaded] = useState(false)
    const [lastWinnerId, setLastWinnerId] = useState(null)
    const confettiRef = useRef(null)

    const onInit = ({ confetti }) => {
        confettiRef.current = confetti
    }

    const fire = () => {
        if (confettiRef.current) {
            confettiRef.current({
                startVelocity: 45,
                decay: 0.92,
                scalar: 1.2,
                spread: 180,
                ticks: 90,
                zIndex: 0,
                particleCount: 300,
                origin: {
                    x: 0.5,
                    y: 1.0
                }
            })
        }
    }

    const getPubs = () => {
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authenticationService.currentUserValue.token}`},
            body: JSON.stringify({'id':id})
        };

        fetch(`${process.env.REACT_APP_API_ROUTES}/wheel/getWheelPubs`, requestOptions)
            .then(handleResponse)
            .then((result) => {
                    setPubs(result)
                    getWheel()
                },
                (error) => {console.log(error)}
            )
    }

    const getWheel = () => {
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authenticationService.currentUserValue.token}`},
            body: JSON.stringify({'id':id})
        };

        fetch(`${process.env.REACT_APP_API_ROUTES}/wheel/getWheel`, requestOptions)
            .then(handleResponse)
            .then((result) => {
                    setWheel(result[0])
                    getModifiers()
                },
                (error) => {console.log(error)}
            )
    }

    const getModifiers = () => {
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authenticationService.currentUserValue.token}`},
            body: JSON.stringify({'id':id})
        };

        fetch(`${process.env.REACT_APP_API_ROUTES}/wheel/getModifiers`, requestOptions)
            .then(handleResponse)
            .then((result) => {
                    setModifiers(result)
                },
                (error) => {console.log(error)}
            )
    }

    const handleWinner = (winner) => {
        // If there was a previous winner and it's different, double its weight back
        if (lastWinnerId == null || lastWinnerId !== winner.id) {
            setPubs(pubs.map(pub =>
                pub.id === lastWinnerId
                    ? { ...pub, weight: pub.weight * 2 }
                    : pub
            ))
            
            // Halve the weight of the current winner (only if different from last winner)
            setPubs(pubs.map(pub =>
                pub.id === winner.id
                    ? { ...pub, weight: pub.weight / 2 }
                    : pub
            ))
        }

        // Update the last winner
        setLastWinnerId(winner.id)

        console.log("Winner:", winner);
        fire();

        const tempMod = selectRandomModifier(winner.id);
        if (tempMod) {
            setSelectedModifier(tempMod);
            setModifiersOpen(true);
        }
    }

    const selectRandomModifier = (winnerPubId) => {
        // Return early if no modifiers
        if (!modifiers || modifiers.length === 0) {
            return null;
        }

        // Filter modifiers that apply to this pub
        const applicableModifiers = modifiers.filter(modifier => {
            // If pub_ids is empty, it's global and applies to all pubs
            let ids = JSON.parse(modifier.pub_ids)
            if (!ids || ids.length === 0) {
                return true;
            }
            // Otherwise, check if the winner pub is in the allowed pub_ids
            return modifier.pub_ids.includes(winnerPubId);
        });

        console.log("Applicable Modifiers:", applicableModifiers);

        // Return null if no applicable modifiers
        if (applicableModifiers.length === 0) {
            return null;
        }

        // Build weighted selection pool
        const weightedPool = [];
        applicableModifiers.forEach(modifier => {
            const weight = modifier.weight || 10; // Default weight is 10
            for (let i = 0; i < weight; i++) {
                weightedPool.push(modifier);
            }
        });

        // Select random modifier from weighted pool
        const randomIndex = Math.floor(Math.random() * weightedPool.length);
        return weightedPool[randomIndex];
    };

    useEffect(() => {
        getPubs()
    }, [])

    useEffect(() => {
        if (!!pubs && !!wheel && pubs.length > 0 && wheel.id > 0) {
            setLoaded(true)
        }
    }, [pubs, wheel])


    if (loaded) {
        return (
            <>
                <Title title={wheel.title} desc={wheel.desc} />

                <div className="w-full max-w-full flex flex-col justify-center items-center">

                    <CssWheel
                        pubs={pubs.filter(p => p.enabled)}
                        onResult={handleWinner}
                    />

                    <button
                        onClick={() => setModifiersOpen(true)}
                        disabled={modifiersOpen}
                        className={`px-8 mt-4 py-3 rounded-full text-white font-bold text-sm shadow-lg transition-all
          ${modifiersOpen ? "bg-gray-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700 active:scale-95"}`}
                    >
                        Open Modifiers
                    </button>

                    <Dialog open={modifiersOpen} onClose={setModifiersOpen} className="relative z-10">
                        <div className="pointer-events-none fixed inset-x-0 bottom-0 flex max-h-full lg:pl-72">
                            <DialogPanel
                                transition
                                className="pointer-events-auto w-full transform transition duration-500 ease-in-out data-[closed]:translate-y-full sm:duration-700"
                            >
                                <div className="relative flex flex-col overflow-y-auto bg-white py-6 shadow-xl rounded-t-lg">
                                    <div className="px-4 sm:px-6">
                                        {JSON.parse(selectedModifer?.pub_ids || "[]").length > 0 ? (
                                            <h3 className="w-max text-sm font-semibold text-gray-900 rounded-full bg-yellow-300 px-2 py-0.5 ring-1 ring-inset ring-yellow-800/20">Pub Modifier - Pub Special</h3>
                                        ) : (
                                            <h3 className="w-max text-sm font-semibold text-gray-900 rounded-full bg-red-400 px-2 py-0.5 ring-1 ring-inset ring-red-800/20">Pub Modifier - Global</h3>
                                        )}
                                        <div className="flex items-start justify-between">
                                            <h3 className="text-2xl font-semibold text-gray-900 p-0">{selectedModifer?.title || ""}</h3>
                                            <div className="ml-3 flex h-7 items-center">
                                                <button
                                                    type="button"
                                                    onClick={() => setModifiersOpen(false)}
                                                    className="relative rounded-md text-gray-400 hover:text-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                                >
                                                    <span className="absolute -inset-2.5" />
                                                    <span className="sr-only">Close panel</span>
                                                    <XMarkIcon aria-hidden="true" className="size-6" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="relative text-gray-600 mt-2 flex-1 sm:text-lg px-4 sm:px-6">{selectedModifer?.description || ""}</div>
                                </div>
                            </DialogPanel>
                        </div>
                    </Dialog>
                </div>

                <ReactCanvasConfetti
                    onInit={onInit}
                />

            </>
        )
    } else {
        return (<Loading />)
    }
}

export default WheelView;